import React, { useEffect, useState } from "react";

/**
 * HeroMomentumWave — visceral multi-strand "Liquid Gold" current.
 *
 * v2 — refinement pass:
 *   • Strand density bumped from 4 → 10. Two heavy halo strands,
 *     three mid-bloom strands, three bright core strands, and two
 *     hairline glints. Varied thickness creates depth without any
 *     single strand becoming the obvious "main line".
 *   • Velocity sped up ~35% across the board (28→18, 36→22, 44→28,
 *     52→32 s) so the field reads as an active current.
 *   • Stardust: 12 → 26 dots, hand-placed in two bands tracking the
 *     wave's vertical envelope so the eye follows them as a stream.
 *   • Pure transform / opacity animations. pointer-events: none.
 *     prefers-reduced-motion → static frame.
 */
const HeroMomentumWave = () => {
  const [reduced, setReduced] = useState(false);
  const rootRef = React.useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // The strands carry SVG Gaussian-blur filters, so every animation frame
  // re-rasterizes the blur. Pause the whole field the moment the hero
  // leaves the viewport so scrolling the rest of the page stays smooth.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([entry]) => {
      el.classList.toggle("hero-wave-paused", !entry.isIntersecting);
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      aria-hidden
      ref={rootRef}
      data-testid="hero-momentum-wave"
      data-reduced={reduced ? "true" : "false"}
      className="hero-wave-root absolute inset-0 pointer-events-none overflow-hidden"
    >
      <svg
        className="hero-wave-svg"
        viewBox="0 0 1600 600"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Bright "Liquid Gold" core — peak champagne in the middle */}
          <linearGradient id="hw-grad-bright" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(212,175,55,0)" />
            <stop offset="32%" stopColor="rgba(243,229,171,0.9)" />
            <stop offset="50%" stopColor="rgba(255,238,189,1)" />
            <stop offset="68%" stopColor="rgba(243,229,171,0.9)" />
            <stop offset="100%" stopColor="rgba(212,175,55,0)" />
          </linearGradient>
          {/* Mid-bloom gold */}
          <linearGradient id="hw-grad-mid" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(212,175,55,0)" />
            <stop offset="42%" stopColor="rgba(212,175,55,0.78)" />
            <stop offset="58%" stopColor="rgba(212,175,55,0.78)" />
            <stop offset="100%" stopColor="rgba(212,175,55,0)" />
          </linearGradient>
          {/* Outer halo — deeper, warmer underlayer */}
          <linearGradient id="hw-grad-halo" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(122,95,18,0)" />
            <stop offset="50%" stopColor="rgba(212,175,55,0.55)" />
            <stop offset="100%" stopColor="rgba(122,95,18,0)" />
          </linearGradient>

          <filter id="hw-soft" x="-10%" y="-50%" width="120%" height="200%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          <filter id="hw-soft-2" x="-10%" y="-50%" width="120%" height="200%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* ===== HALO STRANDS (heaviest blur, lowest opacity) ===== */}
        <g className="hw-strand hw-strand-h1">
          <path
            d="M-200,330 C 100,210 350,440 700,310 C 1050,180 1300,420 1800,290"
            stroke="url(#hw-grad-halo)" strokeWidth="14" fill="none"
            filter="url(#hw-soft-2)"
          />
        </g>
        <g className="hw-strand hw-strand-h2">
          <path
            d="M-200,300 C 200,180 460,420 780,290 C 1080,180 1380,400 1800,260"
            stroke="url(#hw-grad-halo)" strokeWidth="10" fill="none"
            filter="url(#hw-soft-2)"
          />
        </g>

        {/* ===== MID-BLOOM STRANDS ===== */}
        <g className="hw-strand hw-strand-m1">
          <path
            d="M-200,290 C 200,240 450,400 800,300 C 1150,200 1350,380 1800,280"
            stroke="url(#hw-grad-mid)" strokeWidth="5" fill="none"
            filter="url(#hw-soft)"
          />
        </g>
        <g className="hw-strand hw-strand-m2">
          <path
            d="M-200,320 C 240,260 520,420 870,320 C 1180,230 1420,400 1800,300"
            stroke="url(#hw-grad-mid)" strokeWidth="4" fill="none"
            filter="url(#hw-soft)"
          />
        </g>
        <g className="hw-strand hw-strand-m3">
          <path
            d="M-200,270 C 180,220 440,360 760,280 C 1100,200 1340,340 1800,260"
            stroke="url(#hw-grad-mid)" strokeWidth="3" fill="none"
            filter="url(#hw-soft)"
          />
        </g>

        {/* ===== BRIGHT CORE STRANDS (the lines the eye follows) ===== */}
        <g className="hw-strand hw-strand-c1">
          <path
            d="M-200,310 C 150,260 400,380 750,300 C 1100,220 1350,360 1800,290"
            stroke="url(#hw-grad-bright)" strokeWidth="2.4" fill="none"
          />
        </g>
        <g className="hw-strand hw-strand-c2">
          <path
            d="M-200,335 C 200,280 480,410 820,320 C 1170,230 1400,390 1800,310"
            stroke="url(#hw-grad-bright)" strokeWidth="2.0" fill="none"
          />
        </g>
        <g className="hw-strand hw-strand-c3">
          <path
            d="M-200,285 C 160,235 420,355 770,275 C 1120,195 1370,335 1800,265"
            stroke="url(#hw-grad-bright)" strokeWidth="1.6" fill="none"
          />
        </g>

        {/* ===== HAIRLINE GLINTS (the finest details) ===== */}
        <g className="hw-strand hw-strand-g1">
          <path
            d="M-200,355 C 280,300 540,430 880,340 C 1210,250 1430,410 1800,325"
            stroke="url(#hw-grad-bright)" strokeWidth="1" fill="none"
            opacity="0.7"
          />
        </g>
        <g className="hw-strand hw-strand-g2">
          <path
            d="M-200,260 C 220,210 480,330 800,260 C 1140,180 1380,310 1800,250"
            stroke="url(#hw-grad-bright)" strokeWidth="0.8" fill="none"
            opacity="0.6"
          />
        </g>

        {/* ===== STARDUST (26 dots in two bands tracking the current) ===== */}
        <g className="hw-stardust">
          {STARDUST_DOTS.map((d) => (
            <circle
              key={d.id}
              cx={d.x}
              cy={d.y}
              r={d.r}
              fill="rgba(255,238,189,0.9)"
              className={`hw-dot hw-dot-${d.id % 8}`}
              style={{ animationDelay: `${d.delay}s` }}
            />
          ))}
        </g>
      </svg>
    </div>
  );
};

// Pre-computed dust positions across two vertical bands following the
// wave envelope. Hand-tuned so the dots feel like they swim with the
// current rather than floating randomly.
const STARDUST_DOTS = [
  // Upper band (y ≈ 240-290)
  { id: 0,  x: 60,   y: 280, r: 1.4, delay: 0 },
  { id: 1,  x: 160,  y: 265, r: 1.0, delay: 0.4 },
  { id: 2,  x: 260,  y: 245, r: 1.6, delay: 0.8 },
  { id: 3,  x: 380,  y: 260, r: 1.0, delay: 1.2 },
  { id: 4,  x: 500,  y: 280, r: 1.2, delay: 0.5 },
  { id: 5,  x: 620,  y: 260, r: 1.8, delay: 1.6 },
  { id: 6,  x: 760,  y: 245, r: 1.0, delay: 0.2 },
  { id: 7,  x: 900,  y: 235, r: 1.4, delay: 1.0 },
  { id: 8,  x: 1040, y: 220, r: 1.2, delay: 0.7 },
  { id: 9,  x: 1180, y: 245, r: 1.6, delay: 1.4 },
  { id: 10, x: 1300, y: 270, r: 1.0, delay: 0.3 },
  { id: 11, x: 1420, y: 260, r: 1.2, delay: 1.8 },
  { id: 12, x: 1540, y: 280, r: 1.0, delay: 0.9 },
  // Lower band (y ≈ 300-360)
  { id: 13, x: 90,   y: 320, r: 1.2, delay: 1.1 },
  { id: 14, x: 200,  y: 340, r: 1.0, delay: 0.6 },
  { id: 15, x: 330,  y: 355, r: 1.4, delay: 2.0 },
  { id: 16, x: 450,  y: 335, r: 1.6, delay: 0.5 },
  { id: 17, x: 580,  y: 350, r: 1.0, delay: 1.3 },
  { id: 18, x: 700,  y: 325, r: 1.2, delay: 0.8 },
  { id: 19, x: 840,  y: 305, r: 1.8, delay: 1.7 },
  { id: 20, x: 980,  y: 320, r: 1.0, delay: 0.4 },
  { id: 21, x: 1120, y: 335, r: 1.4, delay: 1.5 },
  { id: 22, x: 1260, y: 320, r: 1.0, delay: 1.0 },
  { id: 23, x: 1380, y: 335, r: 1.6, delay: 0.2 },
  { id: 24, x: 1500, y: 320, r: 1.0, delay: 1.9 },
  { id: 25, x: 1580, y: 345, r: 1.2, delay: 0.7 },
];

export default HeroMomentumWave;
